<template lang="pug">
  v-flex.px-0(d-flex)
    v-snackbar(v-model="toggleUnsubscribedError" color="error")
      div(v-html="unsubscribedErrorMessage")
    v-snackbar(v-model="showAttachmentSizeError" :auto-height="true" color="error")
      div.attachment-size-error {{ attachmentSizeErrorText }}
    v-dialog(v-model="sendingOptionDialog" persistent max-width="500")
      v-card
        v-card-title.primary.widget-title Confirm Sending Options
        v-card-text(v-if="isSendingOptionImmediate")
          p Hit SEND to get your email on it's way!
          p Your email will be delivered as soon as possible. If it does not reach the inbox or reflect on your contact activity timeline immediately, please give it some time and do not attempt to resend.
        v-card-text(v-if="isSendingOptionScheduled || isSendingOptionRecurring") Sending email on {{ selectedDateFormatted }} at {{ sendingScheduleTime }}
        v-card-text(v-if="isSendingOptionRecurring") Sending email with intervals of {{ sendRecurring ? sendRecurring.name : '' }}
        v-card-actions
          v-spacer
          v-btn(@click="toggleOpenDialog" flat) Cancel
          v-btn.primary(@click="handleSendEmail" :disabled="isHandlingEmailSending" flat) Send

    v-container.pa-0(fluid)
      v-layout.pa-2(row wrap)
        v-flex(xs12 v-if="!isConsentMode")
          v-radio-group.justify-radio(v-model="sendOptionRadioValue" row)
            v-spacer
            v-radio(:key="1" :value="1" label="Immediately")
            v-radio(:key="2" :value="2" label="Scheduled")
            v-radio(:key="3" :value="3" label="Scheduled and Recurring")
            v-spacer
        v-flex(xs12 v-if="isSendingOptionScheduled || isSendingOptionRecurring")
          span Sending Schedule
          v-layout(wrap)
            v-flex(:class="inputSize")
              v-menu(
                ref="calendarMenu"
                :close-on-content-click="false"
                v-model="sendScheduleCalendarPopup"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              )
                v-text-field(
                  v-model="initialDateRaw"
                  slot="activator"
                  label="Date"
                  prepend-icon="event"
                  readonly
                )
                v-date-picker(
                  v-model="initialDateRaw"
                  @input="$refs.calendarMenu.save(initialDateRaw)"
                  :min="minDate"
                  no-title
                )
            v-flex(:class="inputSize")
              v-combobox(
                v-model="initialTime"
                label="Time"
                prepend-icon="access_time"
                :items="timeSlots()"
                :rules="timeRules"
              )
        v-flex(xs12 v-if="isSendingOptionRecurring")
          span Recurring Schedule
          v-container(pa-0 fluid)
            recurrence-picker(
              v-model="sendRecurring"
              :initialDate="initialDateParsed"
              :inputSize="recurrenceInputSize"
            )

      v-layout.pr-4(xs12)
        v-flex(v-if="showSend" d-flex)
          v-card(flat)
            v-layout(justify-end :class="padding")
              ld-gated-button.primary(
                :feature="LdFeatures.Emails"
                :disabled="isDisabled()"
                @click="toggleOpenDialog"
                :usage="toSelected.length"
              ) Send
    liondesk-dialog(
      v-if="userHasEmailSpamFeatureFlag"
      v-model="hasPhishingError"
      width="480"
      hideCancelButton
      ok-button-text="Ok"
      :dialogTitle="phishingDialogTitle"
      :dialogBody="phishingDialogBody"
      @click:ok="togglePhishingError"
    )
    liondesk-dialog(
      v-if="isConsentMode"
      v-model="consenFormLinkModal"
      width="480"
      :ok-button-text="consentFormLinkDialogOkButtonText"
      :dialogTitle="consentFormLinkDialogTitle"
      :dialogBody="consentFormLinkDialogBody"
      @click:ok="goToTextManagement"
      @click:cancel="consenFormLinkModal = false"
    )
      template(slot="body")
        div.pa-2 It looks like you haven't created your own SMS consent form link, Go to 
          a(:href="textManagementLink") Text Management
          |  and find <strong>SMS Consent Form Builder</strong>.
</template>

<script>
import dateFns from 'date-fns';

import { mapActions, mapState, mapGetters } from 'vuex';
import { timeSlots, timeRules } from '@/Core/utilities';
import { currentDate, formatDate, afterYesterday, split12Time } from '@/Core/filters/dates';
import RecurrencePicker from '@/Communications/components/RecurrencePicker';
import { SEND_OPTIONS } from '@/Communications/config';
import { LdGatedButton, LdFeatures } from 'lion-vue';
import { EventBus } from '@/event-bus';
import { TypeFilters } from '@/Contacts/config/scheduleds-filters';
import LiondeskDialog from '@/Core/components/LiondeskDialog';
import { MAX_ATTACHMENT_SIZE_BYTES, MAX_ATTACHMENT_SIZE_MEGABYTES, RECOMMENDED_ATTACHMENT_SIZE_MEGABYTES } from '../config/email-limits';

const sendRecurringBaseCase = {
  name: null,
  recurrenceRule: null,
  startDate: null,
};

export default {
  name: 'EmailScheduleOptions',
  components: {
    RecurrencePicker,
    LdGatedButton,
    LiondeskDialog,
  },
  props: {
    inputSize: {
      default: 'xs4',
      type: String,
    },
    padding: {
      default: '',
      type: String,
    },
    showSend: {
      default: true,
      type: Boolean,
    },
    sendingOptionDialogOut: {
      default: false,
      type: Boolean,
    },
    recurrenceInputSize: {
      default: 'xs12',
      type: String,
    },
    isConsentMode: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      LdFeatures,
      sendingOptionDialog: false,
      sendOptionRadioValue: 1,
      sendScheduleCalendarPopup: false,
      initialDateRaw: currentDate(),
      initialDateParsed: new Date(),
      initialTime: dateFns.format(new Date(), 'h:mm a'),
      timeRules,
      timeSlots,
      sendRecurring: sendRecurringBaseCase,
      afterYesterday,
      sendingScheduleDate: '',
      sendingScheduleTime: '',
      isHandlingEmailSending: false,
      phishingDialogTitle: 'Email Failed To Send',
      phishingDialogBody:
        'Your emailing activity has been flagged as potentially malicious. Your ability to send emails has been temporarily suspended. Your original emails have been preserved and will be reviewed promptly by support. If validated, your emails will be sent as normal and appear in your contact log.',
      unsubscribedErrorMessage:
        'This contact has unsubscribed. Want to resubscribe them? <a href="https://lonewolf.my.site.com/s/article/LD-Resubscribe-a-contact-LionDesk" target="_blank" style="color:white"><b>Learn how here.</b></a>',
      showedAttachmentSizeError: false,
      attachmentSizeErrorText:
        `The attachments on this message total more than the limit of ${MAX_ATTACHMENT_SIZE_MEGABYTES}MB. For best deliverability, we recommend that email attachments total less than ${RECOMMENDED_ATTACHMENT_SIZE_MEGABYTES}MB or that you put links to the files in the body of your email.`,
      consenFormLinkModal: false,
      consentFormLinkDialogTitle: 'SMS Consent Form Link',
      consentFormLinkDialogOkButtonText: 'Text Management',
      textManagementLink: '/communications/text/text-management#consent-builder',
    };
  },
  computed: {
    ...mapState({
      body: state => state.templates.body,
      subject: state => state.templates.subject,
      toSelected: state => state.recipients.toSelected,
      state: state => state.templates.state,
      isSending: state => state.templates.isSending,
      showUnsubscribedError: state => state.templates.showUnsubscribedError,
      recurrenceRule: state => state.templates.recurrenceRule,
      currentUser: state => state.userInformation.user,
      selectedEmailAccount: state => state.emailAccounts.selectedEmailAccount,
      hasPhishingError: state => state.templates.hasPhishingError,
      optInFormData: state => state.optInFormBuilder.data,
    }),
    ...mapGetters({
      userHasNylasSend: 'userInformation/userHasNylasSend',
      userHasFeatureFlag: 'userInformation/userHasFeatureFlag',
      totalAttachmentSizeBytes : 'templates/totalAttachmentSizeBytes',
      uploadFilesInProgress : 'templates/uploadFilesInProgress',
    }),
    invalidAttachmentSize() {
      return this.totalAttachmentSizeBytes > MAX_ATTACHMENT_SIZE_BYTES;
    },
    showAttachmentSizeError: {
      get() {
        return this.invalidAttachmentSize && !this.showedAttachmentSizeError;
      },
      set() {
        this.showedAttachmentSizeError = true;
      },
    },
    minDate() {
      return currentDate();
    },
    isSendingOptionImmediate() {
      return this.sendOptionRadioValue === SEND_OPTIONS.Immediate;
    },
    isSendingOptionScheduled() {
      return this.sendOptionRadioValue === SEND_OPTIONS.Scheduled;
    },
    isSendingOptionRecurring() {
      return this.sendOptionRadioValue === SEND_OPTIONS.Recurring;
    },
    selectedDateFormatted() {
      return formatDate(this.initialDateRaw);
    },
    selectedDateParsed() {
      return new Date(this.selectedDateFormatted);
    },
    userHasEmailSpamFeatureFlag() {
      return this.userHasFeatureFlag('email_spam_control');
    },
    toggleUnsubscribedError: {
      get() {
        return this.showUnsubscribedError;
      },
      set(value) {
        this.setShowUnsubscribedError(value);
      },
    },
  },

  filters: {
    formatDate,
  },

  watch: {
    initialDateRaw(value) {
      if (value) {
        this.initialDateParsed = new Date(`${value} PST`);
      } else {
        this.initialDateRaw = currentDate();
        this.initialDateParsed = new Date();
      }
    },
    initialDateParsed(value) {
      if (value) {
        this.sendRecurring = sendRecurringBaseCase;
      }
    },
    initialTime(value) {
      if (value && timeRules[0](value) === true) this.sendingScheduleTime = value;
    },
    sendingOptionDialogOut(value) {
      if (value) this.isHandlingEmailSending = false;
      this.sendingOptionDialog = value;
    },
    sendOptionRadioValue(value) {
      this.$emit('change-sending-options', value);
    },
    sendRecurring: {
      handler(value) {
        if (value && value.recurrenceRule && value.startDate) {
          this.setRecurrenceRule(value.recurrenceRule);
          this.sendingScheduleDate = value;
          this.$emit('change-recurrence-rule-valid', this.isSendRecurringValid());
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.sendingScheduleDate = this.initialDateRaw;
    this.sendingScheduleTime = this.initialTime;
  },

  methods: {
    ...mapActions('templates', ['sendEmail', 'setRecurrenceRule', 'setShowUnsubscribedError', 'togglePhishingError']), ...mapActions('emailAccounts', ['resetSelectedEmailAccount']),
    // Conditions where the Send button is disabled
    isDisabled() {
      const emptyBody = !this.body || (this.body && !this.body.trim());
      const emptySubject = !this.subject || (this.subject && !this.subject.trim());
      if (this.userHasNylasSend) {
        return (
          emptyBody ||
          emptySubject ||
          !this.toSelected.length ||
          this.isSending ||
          !this.isScheduledValid() ||
          !this.isSendRecurringValid() ||
          !this.selectedEmailAccount ||
          this.uploadFilesInProgress
        );
      }

      return (
        emptyBody ||
        emptySubject ||
        !this.toSelected.length ||
        this.isSending ||
        !this.isScheduledValid() ||
        !this.isSendRecurringValid() ||
        this.uploadFilesInProgress
      );
    },
    /*
     * Checks if the scheduled option is selected, ensure the user has inputted something
     */
    isScheduledValid() {
      if (this.isSendingOptionScheduled || this.isSendingOptionRecurring) {
        return (
          !!this.initialDateParsed && !!this.initialTime && timeRules[0](this.initialTime) === true
        );
      }
      return true;
    },
    /*
     * Checks if the recurring option is selected, ensure the user has inputted something
     */
    isSendRecurringValid() {
      if (this.isSendingOptionRecurring) {
        return !!this.sendRecurring.recurrenceRule;
      }
      return true;
    },
    toggleOpenDialog() {
      if (this.isConsentMode && this.hasOptFormDataEmpty()) {
        this.consenFormLinkModal = true;
        return;
      }

      if (this.invalidAttachmentSize) {
        this.showedAttachmentSizeError = false;
        return;
      }
      this.$emit('close-sending-dialog');
      this.sendingOptionDialog = !this.sendingOptionDialog;
      this.isHandlingEmailSending = false;
    },
    async handleSendEmail() {
      this.isHandlingEmailSending = true;
      const sendOptions = { type: 'send_now' };
      if (this.isSendingOptionScheduled) {
        const sendSchedule = this.setTime(this.selectedDateParsed);
        sendOptions.sendSchedule = sendSchedule;
        sendOptions.type = 'scheduled_send';
        sendOptions.schedule = {
          action: 'send_email_scheduled',
          type: 'single',
          due_at: sendSchedule,
        };
      }
      if (this.isSendingOptionRecurring) {
        const startDate = this.setTime(this.sendRecurring.startDate);
        sendOptions.type = 'recurring_send';
        sendOptions.schedule = {
          action: 'send_email_scheduled',
          type: 'recurrent',
          due_at: startDate,
          recurrence_rule: this.sendRecurring.recurrenceRule,
        };
      }
      this.$emit('close-sending-dialog');
      const response = await this.sendEmail(sendOptions);

      if (response) {
        this.resetSelectedEmailAccount();
        this.$emit('clear-all');
        this.$emit('email-sent');
        if (this.isSendingOptionScheduled || this.isSendingOptionRecurring) {
          EventBus.$emit('scheduled-communication', TypeFilters.Email);
        }
      }
      this.sendingOptionDialog = false;
    },
    setTime(date) {
      const { hour, minutes } = split12Time(this.sendingScheduleTime);
      date.setHours(hour);
      date.setMinutes(minutes);
      return date;
    },
    goToTextManagement() {
      this.$router.push(this.textManagementLink);
    },
    hasOptFormDataEmpty() {
      return !(this.optInFormData && this.optInFormData.customerCareContact && this.optInFormData.privacyPolicyText && this.optInFormData.privacyPolicyLink);
    },
  },
};
</script>
<style scoped lang="scss">
.justify-radio {
  justify-content: flex-end;
}
.attachment-size-error {
  white-space: normal;
}
</style>
